import { createListenerMiddleware, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { AgentConversationFormActions } from './slice'
import { WS2Actions } from '@/websocket_v2/ws2.slice'
import { WS2SendPayloadType } from '@/websocket_v2/ws2.schemas'
import { AgentAssistantRequestParams } from './schemas'
import { researchFiltersActions } from '../sources-dialog/store/slice'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import {
  selectAgentConversationFormCaselawDateRange,
  selectAgentConversationFormIncludeUnpublishedCases,
  selectAgentConversationFormResearchEnabled,
} from './selectors'
import {
  selectAreAllFederalTabSourcesSelected,
  selectHasRelatedFederalContent,
  selectNumberOfCurrentSourcesShownInFederalTab,
  selectSelectedStateNames,
} from '../sources-dialog/store/selectors'
import { formatRiaDateRange } from '@/analytics/utils/formatRiaDateRange'

export const agentFormListenerMiddleware = createListenerMiddleware()
const startListener = agentFormListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Submit the agent chat form
 */
startListener({
  actionCreator: AgentConversationFormActions.submitForm,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload
    // Get the form data for this conversation
    const formData = listenerApi.getState().agentConversationFormState.forms[conversationId]
    if (!formData) {
      console.error('Form data not found.')
      return
    }

    const state = listenerApi.getState()
    const isResearchEnabled = selectAgentConversationFormResearchEnabled(state, { conversationId })
    if (isResearchEnabled) {
      const trackEvent = getTrackEventGlobal()
      if (trackEvent) {
        const riaSelectedStatesCount = selectSelectedStateNames(state, { conversationId }).length
        const riaSelectedFederalCount = selectNumberOfCurrentSourcesShownInFederalTab(state, { conversationId })
        const riaAllFederalSourcesSelected = selectAreAllFederalTabSourcesSelected(state, { conversationId })
        const riaIncludeFederalContent = selectHasRelatedFederalContent(state, { conversationId })
        const riaIncludeUnpublishedCaseLaw = selectAgentConversationFormIncludeUnpublishedCases(state, { conversationId })

        trackEvent(AnalyticsEvent.RiaMessageWithResearchSubmitted, {
          conversationId,
          riaSelectedStatesCount,
          riaSelectedFederalCount,
          riaAllFederalSourcesSelected,
          riaIncludeFederalContent,
          riaIncludeUnpublishedCaseLaw,
          riaDateRange: formatRiaDateRange(selectAgentConversationFormCaselawDateRange(state, { conversationId })),
        })
      }
    }

    // Mark the form as submitting
    listenerApi.dispatch(AgentConversationFormActions.setQueryIsSubmitting({ conversationId, isSubmitting: true }))

    // Create a deep copy of the request params
    const requestPayload: AgentAssistantRequestParams = JSON.parse(JSON.stringify(formData.requestParams))

    // Clear caselaw date filters and unpublished flag if no jurisdictions selected
    // This ensures we don't send date filters when research is disabled
    if (requestPayload.caselaw_search_filter) {
      const hasJurisdictions = requestPayload.caselaw_search_filter.jurisdiction_codes?.length > 0
      if (!hasJurisdictions) {
        requestPayload.caselaw_search_filter.date_low = null
        requestPayload.caselaw_search_filter.date_high = null
        requestPayload.caselaw_search_filter.include_unpublished_cases = false
      }
    }

    let payloadToSend: AgentAssistantRequestParams | Omit<AgentAssistantRequestParams, 'user_files_rag'>

    // Backend uses the presence of user_files_rag to select the new user-files mode,
    // including when user_file_ids is an empty array.
    if (requestPayload.user_files_rag) {
      // user_files mode and legacy file_paths mode are mutually exclusive.
      payloadToSend = {
        ...requestPayload,
        file_paths: [],
        template_paths: [],
        user_files_rag: {
          user_file_ids: [...requestPayload.user_files_rag.user_file_ids],
        },
      }
    } else {
      const { user_files_rag: _ignored, ...legacyPayload } = requestPayload
      payloadToSend = legacyPayload
    }

    // Websocket: Connect and send
    listenerApi.dispatch(
      WS2Actions.connectAndSend({
        type: WS2SendPayloadType.CONVERSATION_MESSAGE,
        conversationId: conversationId,
        payload: payloadToSend,
      })
    )
  },
})

/**
 * Stop the agent chat process
 */
startListener({
  actionCreator: AgentConversationFormActions.sendStop,
  effect: async (action, listenerApi) => {
    const { conversationId } = action.payload

    // Websocket: Connect and send
    listenerApi.dispatch(WS2Actions.stopTask({ conversationId }))
  },
})

/**
 * Sync research filters to request params
 *
 * This listener watches for ANY change to research filters (state selection,
 * federal content toggles, etc.) and automatically syncs the UI state to the
 * request params that will be sent to the backend.
 *
 * Excludes: syncFromRequestParams (which is used when LOADING conversations)
 */
startListener({
  matcher: (action): action is PayloadAction<{ conversationId: string }> => {
    if (!action.type.startsWith('researchFilters/')) return false
    // Exclude syncFromRequestParams (loading from backend, not syncing to request params)
    if (action.type === 'researchFilters/syncFromRequestParams') return false
    // Exclude toggleStateSelected (source arrays aren't ready yet; sync on applyStateToggleSideEffects instead)
    if (action.type === 'researchFilters/toggleStateSelected') return false
    return true
  },
  effect: (action, listenerApi) => {
    const conversationId = action.payload.conversationId
    if (!conversationId) return

    const state = listenerApi.getState()
    const filters = state.researchFiltersState.filters[conversationId]

    if (filters) {
      listenerApi.dispatch(
        AgentConversationFormActions.syncResearchFiltersToRequestParams({
          conversationId,
          stateLrrSources: filters.stateLrrSources,
          stateCaselawSources: filters.stateCaselawSources,
          federalLrrSources: filters.federalLrrSources,
          federalCaselawSources: filters.federalCaselawSources,
        })
      )
    }
  },
})

/**
 * Sync research filters when request params are restored
 * This happens when loading a conversation from the backend
 */
startListener({
  actionCreator: AgentConversationFormActions.restoreRequestParams,
  effect: async (action, listenerApi) => {
    const { conversationId, requestParams } = action.payload

    const lrrJurisdictionCodes = requestParams.lrr_search_filter?.jurisdiction_codes ?? []
    const caselawJurisdictionCodes = requestParams.caselaw_search_filter?.jurisdiction_codes ?? []

    listenerApi.dispatch(researchFiltersActions.syncFromRequestParams({ conversationId, lrrJurisdictionCodes, caselawJurisdictionCodes }))
  },
})
